import { useTranslation } from "react-i18next";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { useEffect, useState } from "react";
import { isEqual } from "lodash";
import { useDispatch } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";

const YEAR = "year";
const MONTH = "month";
const DAY = "day";
const START = "start";
const END = "end";

const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth(), CURRENT_DATE.getDate());
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const initPeriod = { start: START_DATE, end: END_DATE }
const PeriodFilter = ({ viewName, period, setPeriod, dateType = DAY, maxDate, minDate, shouldDisableStartDate, shouldDisableEndDate }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const [startDate, setStartDate] = useState(period?.start);
  const [endDate, setEndDate] = useState(period?.end);

  const periodMaxDate = maxDate ? new ZDate(maxDate) : null;
  const periodMinDate = minDate ? new ZDate(minDate) : null;

  const onAcceptHandler = (dateValue, type) => {
    if (isEqual(type, START)) {
        dateValue.setHours(0);
        dateValue.setMinutes(0);
        dateValue.setSeconds(0);

        switch (dateType) {
          case YEAR:
            dateValue.setMonth(0);
            dateValue.setDate(1);
            break;
          case MONTH:
            dateValue.setDate(1);
            break;
          default:
            break;
        }

        setStartDate(dateValue);
    } else {
        dateValue.setHours(23);
        dateValue.setMinutes(59);
        dateValue.setSeconds(59);

        switch (dateType) {
          case YEAR:
            dateValue.setMonth(12);
            dateValue.setDate(0);
            break;
          case MONTH:
            dateValue.setDate(1);
            dateValue.setMonth(dateValue.getMonth() + 1);
            dateValue.setDate(0);
            break;
          default:
            break;
        }

        setEndDate(dateValue);
    }
  }

  const getViews = () => {
    if (isEqual(dateType, MONTH)) {
      return [YEAR, MONTH];
    }

    return [YEAR, MONTH, DAY];
  }

  const getInputFormat = () => {
    if (isEqual(dateType, MONTH)) {
      return "yyyy-MM";
    }

    return "yyyy-MM-dd";
  }

  const openTo = () => {
    if (isEqual(dateType, MONTH)) {
      return MONTH;
    }

    return DAY;
  }

  useEffect(() => {
    if (!period) {
      setPeriod(initPeriod);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const changePeriod = { start: startDate, end: endDate };
    const createPeriodReduxState = reduxUtil.createObjectState(viewName, { period: changePeriod });
    reduxDispatch(reduxUtil.updateViewState(createPeriodReduxState));

    setPeriod(changePeriod);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    setStartDate(period?.start);
    setEndDate(period?.end);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} >
      <DatePicker
        label={t("periodStart")}
        views={getViews()}
        format={getInputFormat()}
        openTo={openTo()}
        value={startDate}
        maxDate={periodMaxDate ?? new ZDate(period.end)}
        minDate={periodMinDate ?? START_MIN_DATE}
        shouldDisableDate={shouldDisableStartDate}
        onAccept={(e) => onAcceptHandler(e, START)}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1, width: 160 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={getViews()}
        format={getInputFormat()}
        openTo={openTo()}
        value={endDate}
        maxDate={periodMaxDate ?? END_MAX_DATE}
        minDate={periodMinDate ?? new ZDate(period.start)}
        shouldDisableDate={shouldDisableEndDate}
        onAccept={(e) => onAcceptHandler(e, END)}
        slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1, width: 160 } } } }}
      />
    </LocalizationProvider>
  )
}

export default PeriodFilter;